import { useTranslation } from 'react-i18next';
import { Show } from '../../openapi/model';
import {
    useGetOrdersByReservationId,
    useGetReservation,
} from '../../openapi/reservations/reservations';
import EventCard from '../components/EventCard';
import FeaturedCard from '../components/FeaturedCard';
import BookingSummaryTable from '../step5/BookingSummaryTable';
import CancellationPolicy from '../step5/CancellationPolicy';
import NeedToKnow from '../step5/NeedToKnow';
import ReservationOrders from './ReservationOrders';
import { useCallback, useEffect, useState } from 'react';
import { useGetRefundChunks } from '../../openapi/bookings/bookings';
import ReservationPendingOrder from './ReservationPendingOrder';
import RefundChunksBlock from './RefundChunksBlock';
import ReservationTickets from './ReservationTickets';
import { ReservationContactInfo } from './ReservationContactInfo';
import TimeslotCancelledBanner from '../components/TimeslotCancelledBanner';
import { useCountdown } from '../hooks/useCountdown';
import { useGetTicketOptions } from '../../openapi/ticket-options/ticket-options';
import { useGetTicketTypes } from '../../openapi/ticket-types/ticket-types';

export default function ReservationDetails({
    show,
    reservationId,
    reservationHash,
    clearReservationUrlParams: _clearReservationUrlParams,
}: {
    show: Show;
    reservationId: number;
    reservationHash: string;
    clearReservationUrlParams: () => void;
}) {
    const [showCancelInformations, setShowCancelInformations] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any | null>(null);
    const [selectedAmount, setSelectedAmount] = useState<number>(0);
    const { setCountDown } = useCountdown();
    const { t } = useTranslation();
    const {
        data: reservation,
        isLoading,
        error,
        refetch,
    } = useGetReservation(reservationId, { reservationHash });
    const {
        data: dataOrders,
        isLoading: isLoadingOrder,
        refetch: refetchOrders,
        error: errorOrders,
    } = useGetOrdersByReservationId(reservationId, {
        reservationHash,
    });
    const {
        data: refundChunks,
        isLoading: isRefundChunksLoading,
        error: refundChunksError,
        refetch: refetchRefundChunks,
    } = useGetRefundChunks(reservationId, { reservationHash });

    // Ticket type/option catalogs so we can render a name for each pending
    // order's ticket when the step-1 cart store is empty (e.g. incognito
    // reload of a reservation URL).
    const { data: ticketTypesData } = useGetTicketTypes({
        optionalId: show.id as unknown as number,
        includeSeats: true,
        onlyShowInList: false,
    });
    const { data: ticketOptionsData } = useGetTicketOptions({
        optionalId: show.id as unknown as number,
    });

    const reloadReservation = useCallback(() => {
        refetch();
    }, [refetch]);

    const reloadOrdersAndReservation = useCallback(async () => {
        // Await the orders refetch because callers (e.g. ReservationPendingOrder
        // after a successful split-payment leg) sequence follow-up state changes
        // against the fresh Stripe SetupIntent client secret that only this
        // endpoint refreshes. The other two refetches are cosmetic and can run
        // in the background.
        reloadReservation();
        refetchRefundChunks();
        await refetchOrders();
    }, [reloadReservation, refetchOrders, refetchRefundChunks]);

    // if there is an order with pending status and a future rollbacktime, we need to show the timeout
    useEffect(() => {
        if (!dataOrders) {
            return;
        }
        const tickingOrder = dataOrders.orders.find(
            (order) => (order.status as unknown as string) === 'pending' && order.rollbackTimeAt,
        );
        if (!tickingOrder) {
            return;
        }
        setCountDown(dataOrders.serverTime as string, tickingOrder.rollbackTimeAt as string);
    }, [dataOrders, setCountDown]);

    if (error || errorOrders || refundChunksError) {
        return (
            <div className="fireknife-widget-error">
                <p>{error?.message || errorOrders?.message || refundChunksError?.message}</p>
            </div>
        );
    }

    if (
        isLoading ||
        isLoadingOrder ||
        isRefundChunksLoading ||
        !reservation ||
        !dataOrders ||
        !refundChunks
    ) {
        return <div className="fireknife-widget-loading"></div>;
    }

    const pendingOrder = dataOrders.orders.find(
        (order) => (order.status as unknown as string) === 'pending',
    );

    // While a modification is in flux the booking itself is still 'finished',
    // but showing the green "Finished"/success badge then is misleading.
    // Surface 'pending' instead. Gate on the pending order's rollbackTimeAt -
    // the same predicate that starts the countdown above - so the badge tracks
    // the ticking counter exactly and never flips a finished booking to
    // "pending" for a pending order that has no counter running.
    const displayStatus =
        pendingOrder?.rollbackTimeAt && (reservation.status as unknown as string) === 'finished'
            ? 'pending'
            : reservation.status;

    // Build a cart-shaped list of just the tickets the pending order adds,
    // so the Booking total breakdown reflects what's being paid now rather
    // than the stale step-1 useCart() store (which may be empty in incognito
    // or contain leftovers from an earlier booking flow).
    const pendingCart = (() => {
        if (!pendingOrder || !Array.isArray(pendingOrder.ticketsAdded)) return undefined;
        const tickets = (reservation?.cart || []).filter((t: any) =>
            pendingOrder.ticketsAdded.includes(t.id),
        );
        if (tickets.length === 0) return undefined;
        const grouped = new Map<string, any>();
        for (const t of tickets) {
            // BookingSummaryTable re-runs calculateOnNet on this `price`, so
            // it needs the NET value. `originalPrice` is the net stored on
            // BookingItems; `price` on the response is gross (net+tax+fee)
            // for backward compat with other consumers.
            const netPrice = (t as any).originalPrice ?? t.price;
            const key = `${t.ticketTypeId}-${t.ticketOptionId}-${netPrice}`;
            const ticketType = ticketTypesData?.ticketTypes.find(
                (tt: any) => tt.id === t.ticketTypeId,
            );
            const ticketOption = ticketOptionsData?.ticketOptions.find(
                (to: any) => to.id === t.ticketOptionId,
            );
            if (!grouped.has(key)) {
                grouped.set(key, {
                    ticketTypeId: t.ticketTypeId,
                    ticketTypeName: ticketType?.name || '',
                    ticketTypeNameI18n: ticketType?.nameI18n,
                    ticketOptionName: ticketOption?.name || '',
                    ticketOptionNameI18n: ticketOption?.nameI18n,
                    price: netPrice,
                    quantity: 0,
                });
            }
            grouped.get(key).quantity += 1;
        }
        return Array.from(grouped.values());
    })();

    const hasRefundingOrders =
        dataOrders.orders.some((order) => (order.status as unknown as string) === 'refunding') &&
        refundChunks.chunks.length > 0;

    const canBeCancelled =
        (reservation.status as unknown as string) === 'finished' &&
        reservation.guestCancellationEnabled !== false;

    // Total already paid (settled transactions), in major units. Used as the
    // value of the GA4 `refund` event when a guest cancels for a full refund.
    const paidValue =
        dataOrders.orders.reduce(
            (sum, o) =>
                sum +
                (o.transactions || []).reduce(
                    (s: number, trx: any) => s + (trx.finished ? trx.amount : 0),
                    0,
                ),
            0,
        ) / 100;

    const olderOrders = dataOrders?.orders.filter(
        (order) => (order.status as unknown as string) !== 'pending',
    );

    const timeslotCancellationReason = reservation.timeslotCancellationReason;

    return (
        <div className="fireknife-step fireknife-step5">
            <div className="fireknife-step__main fireknife-step5__main">
                {timeslotCancellationReason && timeslotCancellationReason.trim() && (
                    <TimeslotCancelledBanner reason={timeslotCancellationReason} />
                )}
                <div className="fireknife-step5__content-header">
                    <div className="fireknife-step5__event-boxes">
                        <div className="fireknife-step5__event-box fireknife-step5__event-box--1">
                            <EventCard
                                show={show}
                                startTime={reservation.startTime}
                                reservationId={reservationId}
                            />
                        </div>
                        <div className="fireknife-step5__event-box fireknife-step5__event-box--2">
                            <FeaturedCard
                                title={t('widgetReservationInfo.status')}
                                desc={t('widgetReservationInfo.statusValues.' + displayStatus)}
                                extraClassName={`fireknife-bookings__status fireknife-bookings__status--${displayStatus}`}
                            />
                        </div>
                    </div>
                </div>
                <div className="fireknife-step5__content-body">
                    {/* Payment */}
                    {pendingOrder && !showCancelInformations && (
                        <>
                            <h2 className="fireknife-step5__title">
                                {t('widgetReservationInfo.payment')}
                            </h2>

                            <BookingSummaryTable
                                isBreakingOutFees={show.isBreakingOutFees}
                                priceListingMode={show.priceListingMode}
                                showFees={show.fees}
                                pickedPaymentMethod={selectedPaymentMethod}
                                paymentMethods={dataOrders?.paymentMethods as any[]}
                                absorbProcessingFee={!!show.marketing?.absorbProcessingFee}
                                orderAmount={selectedAmount}
                                cartOverride={pendingCart}
                                discountOverride={
                                    pendingOrder
                                        ? {
                                              amount:
                                                  ((pendingOrder as any)
                                                      .discountAmount as number) || 0,
                                              // Server only returns
                                              // couponCode for the
                                              // first (guest-entered)
                                              // order; admin-applied
                                              // coupons come through
                                              // as null.
                                              code:
                                                  ((pendingOrder as any).couponCode as
                                                      | string
                                                      | null) || null,
                                          }
                                        : undefined
                                }
                            />

                            <ReservationPendingOrder
                                order={pendingOrder}
                                allOrders={dataOrders.orders}
                                paymentMethods={dataOrders.paymentMethods as any[]}
                                reservationId={reservationId}
                                reservationHash={reservationHash}
                                reloadOrdersAndReservation={reloadOrdersAndReservation}
                                show={show}
                                onPaymentMethodChange={setSelectedPaymentMethod}
                                onAmountChange={setSelectedAmount}
                                cart={pendingCart}
                            />
                        </>
                    )}
                    {/* Refund chunks */}
                    {hasRefundingOrders && !showCancelInformations && (
                        <RefundChunksBlock
                            orders={dataOrders.orders}
                            reservationId={reservationId}
                            refundChunks={refundChunks.chunks as any[]}
                            refundChunksError={refundChunksError}
                            reloadOrdersAndReservation={reloadOrdersAndReservation}
                            reservationHash={reservationHash}
                            paymentMethods={dataOrders.paymentMethods as any[]}
                            venueEmail={show.marketing?.email ?? null}
                            venuePhone={show.marketing?.phone ?? null}
                        />
                    )}

                    <div className="fireknife-summary-data">
                        {/* Tickets */}
                        {!showCancelInformations && (
                            <ReservationTickets
                                reservation={reservation}
                                pendingOrder={pendingOrder}
                            />
                        )}

                        {/* Contact info */}
                        {!showCancelInformations && (
                            <ReservationContactInfo reservation={reservation} />
                        )}
                        {/* Boring log of orders + transactions */}
                        {!showCancelInformations &&
                            dataOrders?.orders.filter(
                                (order) => (order.status as unknown as string) !== 'pending',
                            ).length > 0 && (
                                <h2 className="fireknife-step5__title">
                                    {t('widgetReservationInfo.payment')}
                                </h2>
                            )}
                        {!showCancelInformations && olderOrders.length > 0 && (
                            <div className="fireknife-reservation-payment">
                                {olderOrders.map((order, idx) => {
                                    return (
                                        <ReservationOrders
                                            key={idx}
                                            order={order}
                                            paymentMethods={dataOrders?.paymentMethods ?? []}
                                        />
                                    );
                                })}
                            </div>
                        )}
                        {/* Cancellation block */}
                        <CancellationPolicy
                            reservation={reservation}
                            reservationId={reservationId}
                            reloadOrdersAndReservation={reloadOrdersAndReservation}
                            setShowCancelInformations={setShowCancelInformations}
                            reservationHash={reservationHash}
                            canBeCancelled={canBeCancelled}
                            paidValue={paidValue}
                        />
                        {/* Need to know */}
                        <NeedToKnow reservation={reservation} />
                    </div>
                </div>
            </div>
        </div>
    );
}
